// SHARED image-upload contract for every form whose images are parsed by
// POST /parse-image → Gemini vision: concept cards AND packaging shots.
//
// PNG + JPEG only. WebP and HEIC were dropped — reported as unreliable in practice
// (they don't render / cause trouble in some paths), so we accept only the two
// universally-safe formats. GIF is not supported by Gemini; PDF/PPT are not images.
// The backend enforces the same list (routes_fastapi/simulation.py ALLOWED_MIME_TYPES) —
// keep the two in lockstep.
//
// This exists because the forms used to disagree three ways: Concept advertised 50MB
// PNG/JPG/WebP/HEIC while Packaging advertised 10MB PNG/JPG, hardcoded a narrower
// `accept`, and then let ANY `image/*` through its runtime filter (#75).
//
// The SIZE cap of 10MB is a deliberate product cap (the backend MAX_FILE_SIZE is 50MB) —
// STRICTER than the backend, never looser.
export const MAX_UPLOAD_SIZE_MB = 10;

export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];

export const ALLOWED_IMAGE_EXTENSIONS_RE = /\.(jpg|jpeg|png)$/i;

export const ACCEPTED_IMAGE_INPUT = [...ALLOWED_IMAGE_MIME_TYPES, '.jpg', '.jpeg', '.png'].join(
  ',',
);

export const UPLOAD_HINT_TEXT = `PNG · JPG — up to ${MAX_UPLOAD_SIZE_MB}MB each`;
export const UPLOAD_HINT_TEXT_SINGLE = `PNG · JPG — up to ${MAX_UPLOAD_SIZE_MB}MB (1 image only)`;

export const UNSUPPORTED_FORMAT_MESSAGE = 'Unsupported format — please use a PNG or JPG image.';
export const FILE_TOO_LARGE_MESSAGE = `Image is too large — the max is ${MAX_UPLOAD_SIZE_MB}MB.`;

// The single validation gate for every /parse-image uploader. Returns a
// user-facing rejection reason, or null when the file is acceptable.
//
// A file passes the format check when its MIME type is explicitly allowed, or —
// only when the browser reports no MIME type — its extension is allowed. This
// deliberately rejects a WebP/HEIC renamed `.png` (non-empty disallowed MIME),
// which the backend would 400 on anyway.
export function imageRejectionReason(file: File): string | null {
  const mimeAllowed = ALLOWED_IMAGE_MIME_TYPES.includes(file.type);
  const extAllowed = ALLOWED_IMAGE_EXTENSIONS_RE.test(file.name);
  if (!mimeAllowed && !(file.type === '' && extAllowed)) {
    return UNSUPPORTED_FORMAT_MESSAGE;
  }
  if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
    return FILE_TOO_LARGE_MESSAGE;
  }
  return null;
}

/**
 * One sentence naming what a picker refused, or null when everything was accepted.
 *
 * Both packaging forms already computed the rejection and then threw it away: files that
 * failed the format/size gate, or that exceeded the cap, simply never appeared. The user
 * picked five images, saw two, and had no way to tell which two were missing or why —
 * the dead (Gemini) form actually alerted, so the reachable forms were a regression
 * (#463 F2-07).
 *
 * Names the FILES, not just the count: "2 files were skipped" leaves the user re-picking
 * everything to work out which.
 */
export function describeRejectedUploads(
  rejected: Array<{ name: string; reason: string }>,
  overCapCount = 0,
  cap?: number,
): string | null {
  const parts: string[] = [];

  // Group by reason so five over-size files read as one sentence, not five.
  const byReason = new Map<string, string[]>();
  rejected.forEach(({ name, reason }) => {
    byReason.set(reason, [...(byReason.get(reason) || []), name]);
  });
  byReason.forEach((names, reason) => parts.push(`${names.join(', ')}: ${reason}`));

  if (overCapCount > 0 && cap !== undefined) {
    parts.push(
      `${overCapCount} more ${overCapCount === 1 ? 'image was' : 'images were'} not added — ` +
        `the limit is ${cap}.`,
    );
  }

  return parts.length ? parts.join(' ') : null;
}
